/**
 * Fills — one row per match, and a total that is `bigint`.
 *
 * **A single order sweeping three price levels produces three fills, not one
 * averaged fill** (§7.12). That is why this is a log rather than a keyed store,
 * why nothing here upserts, and why the tape reads newest-first: a fill is final
 * the moment the sequencer accepts it and is never revised.
 *
 * ## The total is the trap invariant 6 names
 *
 * > Cumulative money is `bigint`, **including derived quantities** (§F18). Twelve
 * > 6dp levels summed with `+=` produce twelve decimal places and `formatShares`
 * > refuses them. A fills table totalling notional is the same trap.
 *
 * So the footer's notional is `parseFixed` into micro-USDC, summed as `bigint`,
 * and formatted once at the end. `Number(a) + Number(b)` over six-decimal
 * strings is the version of this that looks right and reconciles wrong.
 *
 * `fee` and `rebate` are **always present and always `0.000000` at launch**, and
 * are rendered from what the server sent rather than assumed (§7.12, D12).
 *
 * ## A fill the chain refused — §F57
 *
 * A `matchOrders` can be mined and **revert**, at which point the venue unwinds
 * the position and returns the collateral, and the fill carries
 * `reverted_at_ms`. The row **stays** — a fill that silently changed the
 * position it published would be the same class of defect as the one the field
 * exists to report — and it is marked.
 *
 * **And it is excluded from the totals**, which is the part that is money
 * rather than decoration: the footer is what this account actually moved, and
 * a reverted fill moved nothing. Counting it would make the footer agree with
 * the rows and disagree with the wallet, which is exactly the shape the whole
 * incident had.
 *
 * **No longer produced since `docs/06` §F94**: a market settles as one net line
 * per trader and nothing on chain refuses a fill, a frozen account's included.
 * A fill recorded before it can still carry the field, so the marker stays.
 */

import { memo, useMemo } from 'react'
import { EmptyState, Glyph, Table } from '@/components/primitives'
import type { TableColumn } from '@/components/primitives'
import { cx } from '@/lib/cx'
import { formatCents, formatShares, formatUsdc } from '@/lib/format'
import { formatFixed, parseFixed, USDC_DP } from '@/lib/decimal'
import { useFills } from '@/app/use-runtime'

type Row = Record<string, unknown>

export const FillsTable = memo(function FillsTable({ className }: { className?: string }) {
  const { rows: fills } = useFills<Row>()

  // Exact, and in one pass. `sum` is micro-USDC and never leaves `bigint` until
  // the single `formatUsdc` below.
  const totals = useMemo(() => {
    let notional = 0n
    let fees = 0n
    for (const fill of fills) {
      // Skipped, not subtracted: a reverted fill moved no money at all, so it
      // contributes nothing rather than contributing and being taken back.
      if (isReverted(fill)) continue
      notional += microUsdc(fill.notional)
      fees += microUsdc(fill.fee)
    }
    return { notional, fees }
  }, [fills])

  const columns = useMemo<TableColumn<Row>[]>(
    () => [
      {
        id: 'time',
        header: 'Time',
        cell: (row) => <span className="ud-num text-text-secondary">{clockText(row.executed_at_ms)}</span>,
      },
      {
        id: 'market',
        header: 'Market',
        card: 'title',
        hideBelowLg: true,
        cell: (row) => (
          <span className="truncate text-body-sm" title={String(row.market_id ?? '') || undefined}>
            {String(row.market_id ?? '')}
          </span>
        ),
      },
      {
        id: 'outcome',
        header: 'Outcome',
        card: 'title',
        cell: (row) => {
          const outcome = String(row.outcome)
          return (
            <span className={cx('flex items-center gap-1', outcome === 'UP' ? 'text-up-text' : 'text-down-text')}>
              <Glyph name={outcome === 'UP' ? 'up' : 'down'} label={null} />
              {outcome}
            </span>
          )
        },
      },
      { id: 'side', header: 'Side', card: 'title', cell: (row) => String(row.side ?? '') },
      {
        id: 'price',
        header: 'Price',
        numeric: true,
        cell: (row) => (typeof row.price_cents === 'number' ? formatCents(row.price_cents) : '—'),
      },
      {
        id: 'size',
        header: 'Size',
        numeric: true,
        cell: (row) => formatShares(String(row.size ?? '0')),
      },
      {
        id: 'notional',
        header: 'Notional',
        numeric: true,
        cell: (row) => (typeof row.notional === 'string' ? formatUsdc(row.notional) : '—'),
      },
      {
        id: 'fee',
        header: 'Fee',
        numeric: true,
        // Rendered from the wire, never assumed. The rates are in `/config` and
        // a client that omits the column because it is zero today breaks on a
        // config change (§7.12).
        cell: (row) => (typeof row.fee === 'string' ? formatUsdc(row.fee) : '—'),
      },
      {
        id: 'liquidity',
        header: '',
        card: 'badge',
        width: 'max-content',
        cell: (row) =>
          isReverted(row) ? (
            // The one place a fill says something other than what it agreed.
            // `title` rather than a second line, because the row set is fixed
            // and the tape is read at a glance.
            <span
              className="text-caption text-down-text"
              title="The transaction that would have settled this fill was refused on chain. The position and the collateral have been returned."
            >
              REVERTED
            </span>
          ) : (
            <span className="text-caption text-text-tertiary">{String(row.liquidity ?? '')}</span>
          ),
      },
    ],
    [],
  )

  return (
    <div className={cx('flex min-h-0 flex-col', className)}>
      <Table
        label="fills"
        columns={columns}
        rows={fills}
        rowKey={(row) => String(row.fill_id)}
        empty={<EmptyState>No fills in the last 7 days. Older results are under Settled.</EmptyState>}
      />
      {fills.length > 0 ? (
        <div className="flex justify-end gap-4 border-t border-border-subtle px-2 py-1_5 text-caption text-text-secondary">
          <span>
            Notional <span className="ud-num text-text-primary">{formatUsdc(formatFixed(totals.notional, USDC_DP))}</span>
          </span>
          <span>
            Fees <span className="ud-num text-text-primary">{formatUsdc(formatFixed(totals.fees, USDC_DP))}</span>
          </span>
        </div>
      ) : null}
    </div>
  )
})

// ---------------------------------------------------------------------------

/**
 * Whether the chain refused this fill's `matchOrders` — §F57.
 *
 * **Read as "present and non-null", never as a truthy number.** `openapi.Fill`
 * types it `integer | null` and omits it entirely on a venue with no chain, so
 * the three absent shapes — missing, `null`, `undefined` — all mean the same
 * thing, and nothing else does.
 */
function isReverted(fill: Record<string, unknown>): boolean {
  return typeof fill.reverted_at_ms === 'number'
}

/** A 6dp USDC string → micro-USDC. An unparseable value contributes nothing
 *  rather than poisoning the total with a `NaN`. */
function microUsdc(value: unknown): bigint {
  if (typeof value !== 'string') return 0n
  try {
    return parseFixed(value, USDC_DP)
  } catch {
    return 0n
  }
}

/**
 * `21:46:03`, in the viewer's own zone.
 *
 * Not from `lib/clock` — this is a wall-clock *label* on a past event, not the
 * countdown's `expiry − t_server_estimate`. Invariant 7 is about the countdown's
 * source; a fill's timestamp arrives on the fill.
 */
function clockText(atMs: unknown): string {
  if (typeof atMs !== 'number') return '—'
  const d = new Date(atMs)
  return [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map((n) => String(n).padStart(2, '0'))
    .join(':')
}
